import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Calendar, 
  Search, 
  Phone, 
  Mail, 
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  User,
  Building2,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import GlobalLoader from "../../components/ui/GlobalLoader";
import axios from "axios";
import axiosInstance from "@/api/axiosInstance";

interface DemoRequest {
  _id: string;
  pharmacyName: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  note: string;
  preferredTimeToContact: string;
  preferredDate: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export function DemoRequestsPage() {
  const [demoRequests, setDemoRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("pending");

  // Fetch demo requests
  const fetchDemoRequests = async () => {
    try {
      setLoading(true);
      const baseURL = import.meta.env.VITE_API_BASE_URL;
      const response = await axiosInstance.get(`${baseURL}/demo-requests`);
      
      if (response.data.success) {
        setDemoRequests(response.data.data.demoRequests || []);
      }
    } catch (error) {
      console.error('Error fetching demo requests:', error);
      toast.error('Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemoRequests();
  }, []);

  // Update demo request status
  const updateDemoRequestStatus = async (id: string, status: 'pending' | 'approved' | 'rejected') => {
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL;
      const response = await axiosInstance.patch(
        `${baseURL}/demo-requests/${id}/status`,
        { status },
      );
      
      if (response.data.success) {
        setDemoRequests(prev => 
          prev.map(req => 
            req._id === id ? { ...req, status } : req
          )
        );
        toast.success(`Demo request ${status} successfully`);
      }
    } catch (error) {
      console.error('Error updating demo request status:', error);
      toast.error('Failed to update demo request status');
    }
  };

  // Delete demo request
  const deleteDemoRequest = async (id: string) => {
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL;
      const response = await axiosInstance.delete(`${baseURL}/demo-requests/${id}`);
      
      if (response.data.success) {
        setDemoRequests(prev => prev.filter(req => req._id !== id));
        toast.success('Demo request deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting demo request:', error);
      toast.error('Failed to delete demo request');
    }
  };

  // Filter demo requests
  const filteredDemoRequests = demoRequests.filter(request => {
    const matchesSearch = 
      request.pharmacyName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      request.contactPersonName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.contactEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.contactPhone.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter !== "all") {
      matchesStatus = request.status === statusFilter;
    }
    
    return matchesSearch && matchesStatus;
  });

  // Filter by tab
  const getTabFilteredRequests = (tab: string) => {
    if (tab === "all") return filteredDemoRequests;
    return filteredDemoRequests.filter(req => req.status === tab);
  };

  const tabFilteredRequests = getTabFilteredRequests(activeTab);

  // Pagination
  const totalPages = Math.ceil(tabFilteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = tabFilteredRequests.slice(startIndex, endIndex);

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  function formatPreferredDate(dateString: string) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <GlobalLoader 
        isLoading={true} 
        message="Loading demo requests..." 
        subMessage="Please wait" 
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Demo Requests</h1>
          <p className="text-gray-600 mt-1">Manage pharmacy demo requests</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="all" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50">
            <Building2 className="w-4 h-4 mr-2" />
            All Requests
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50">
            <Clock className="w-4 h-4 mr-2" />
            Pending
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50">
            <CheckCircle className="w-4 h-4 mr-2" />
            Approved
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50">
            <XCircle className="w-4 h-4 mr-2" />
            Rejected
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search demo requests..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-11"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Demo Requests List */}
          <div className="space-y-4">
            {paginatedRequests.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No demo requests found</p>
                </CardContent>
              </Card>
            ) : (
              paginatedRequests.map((request) => (
                <Card key={request._id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{request.pharmacyName}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 my-1">
                            <div className="flex items-center space-x-1">
                              <User className="w-4 h-4" />
                              <span>{request.contactPersonName}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{request.contactEmail}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Phone className="w-4 h-4" />
                              <span>{request.contactPhone}</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 my-1">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>Time: {request.preferredTimeToContact}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>Date: {request.preferredDate ? formatPreferredDate(request.preferredDate) : 'Not specified'}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>Submitted: {formatDate(request.createdAt)}</span>
                            </div>
                          </div>

                          {request.note && (
                            <div className="flex items-start space-x-1 mt-2">
                              <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                              <p className="text-sm text-gray-600 italic">"{request.note}"</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div>
                          {getStatusBadge(request.status)}
                        </div>
                        <div className="flex space-x-2">
                          {request.status === 'pending' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => updateDemoRequestStatus(request._id, 'approved')}
                                className="text-green-600 hover:text-green-700 border-green-600"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => updateDemoRequestStatus(request._id, 'rejected')}
                                className="text-red-600 hover:text-red-700 border-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => deleteDemoRequest(request._id)}
                            className="text-red-600 hover:text-red-700 border-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {Math.min(endIndex, tabFilteredRequests.length)} of {tabFilteredRequests.length} requests
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
